import React, { useState, useEffect } from 'react'
import './CTA.css'

function CTA() {
  const [userOS, setUserOS] = useState('mac')
  const [version, setVersion] = useState(null)

  useEffect(() => {
    // Detect user's operating system
    const platform = navigator.platform.toLowerCase()
    const userAgent = navigator.userAgent.toLowerCase()

    if (platform.includes('win') || userAgent.includes('windows')) {
      setUserOS('windows')
    } else {
      setUserOS('mac')
    }

    // Fetch version info
    fetch('/downloads/version.json?t=' + Date.now())
      .then(res => res.json())
      .then(data => setVersion(data.version))
      .catch(() => setVersion(null))
  }, [])

  const baseUrl = 'https://vibecodingmachine-website.s3.amazonaws.com/downloads'

  // Construct URLs with version if available, otherwise fallback to generic
  const macUrl = version
    ? `${baseUrl}/VibeCodingMachine-${version}.dmg`
    : `${baseUrl}/VibeCodingMachine.dmg`

  const winUrl = version
    ? `${baseUrl}/VibeCodingMachine-${version}-win32-x64.zip`
    : `${baseUrl}/VibeCodingMachine-win32-x64.zip`

  const downloads = {
    mac: {
      primary: {
        label: 'Download for macOS',
        url: macUrl
      },
      secondary: null,
      installer: {
        text: "Don't have npm? ",
        linkText: 'macOS Installer (.pkg)',
        url: 'https://nodejs.org/en/download'
      }
    },
    windows: {
      primary: {
        label: 'Download for Windows',
        url: winUrl
      },
      secondary: {
        label: 'Download for macOS',
        url: macUrl
      },
      installer: {
        text: "Don't have npm? ",
        linkText: 'Node.js Installer',
        url: 'https://nodejs.org/en/download'
      }
    }
  }

  const currentDownload = downloads[userOS]

  return (
    <section id="download" className="cta">
      <div className="container">
        <div className="cta-content">
          <h2 className="cta-title">
            Ready to bring your <span className="gradient-text">dreams</span> to life?
          </h2>
          <p className="cta-description">
            Join developers who are shipping faster with autonomous AI workflows.
            Get started today and let Vibe Coding Machine handle the heavy lifting.
          </p>
          <div className="cta-buttons" style={{ flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
            {version && (
              <div style={{
                fontSize: '0.85rem',
                color: 'rgba(255, 255, 255, 0.5)',
                marginBottom: '-4px',
                fontFamily: 'Monaco, monospace'
              }}>
                v{version}
              </div>
            )}
            <a href={currentDownload.primary.url} className="btn btn-primary btn-large">
              {currentDownload.primary.label}
            </a>
            {userOS === 'windows' && (
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                padding: '20px 24px',
                maxWidth: '600px',
                marginTop: '8px',
                textAlign: 'left'
              }}>
                <h3 style={{
                  fontSize: '1rem',
                  fontWeight: '600',
                  marginBottom: '12px',
                  color: 'rgba(255, 255, 255, 0.95)'
                }}>
                  📦 Installation Instructions
                </h3>
                <ol style={{
                  fontSize: '0.9rem',
                  lineHeight: '1.8',
                  color: 'rgba(255, 255, 255, 0.85)',
                  paddingLeft: '20px',
                  margin: 0
                }}>
                  <li><strong>Download</strong> the ZIP file above<br />
                    <span style={{ fontSize: '0.85rem', opacity: '0.9', fontStyle: 'italic' }}>
                      ⚠️ If you see "Insecure download blocked" - Click "Keep" to proceed
                    </span>
                  </li>
                  <li><strong>Extract</strong> to a permanent location like:<br />
                    <code style={{
                      background: '#1f2937',
                      color: '#10b981',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '0.85rem',
                      marginTop: '4px',
                      display: 'inline-block'
                    }}>C:\Program Files\VibeCodingMachine\</code>
                  </li>
                  <li><strong>Run</strong> VibeCodingMachine.exe from the extracted folder<br />
                    <span style={{ fontSize: '0.85rem', opacity: '0.9', fontStyle: 'italic' }}>
                      ⚠️ You may see "Windows protected your PC" - Click "More info" → "Run anyway"
                    </span>
                  </li>
                  <li><strong>Create shortcut:</strong> Right-click the .exe → "Create shortcut" → Move to Desktop</li>
                  <li><strong>Pin to taskbar:</strong> Right-click the .exe → "Pin to taskbar"</li>
                </ol>
                <div style={{
                  marginTop: '16px',
                  padding: '12px',
                  background: 'rgba(59, 130, 246, 0.1)',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  lineHeight: '1.6'
                }}>
                  <strong style={{ color: 'rgba(147, 197, 253, 1)' }}>💡 Why the SmartScreen warning?</strong><br />
                  The app is not yet code-signed with a Windows certificate. This is normal for new software.
                  The warning will disappear in future versions once we obtain code signing.
                </div>
              </div>
            )}
            {currentDownload.secondary && (
              <a
                href={currentDownload.secondary.url}
                className="btn btn-secondary-download"
                style={{
                  padding: '12px 32px',
                  fontSize: '0.9rem',
                  opacity: '0.7',
                  background: 'transparent',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  color: 'rgba(255, 255, 255, 0.8)',
                  borderRadius: '8px',
                  transition: 'all 0.3s ease',
                  textDecoration: 'none'
                }}
                onMouseEnter={(e) => {
                  e.target.style.opacity = '1'
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)'
                }}
                onMouseLeave={(e) => {
                  e.target.style.opacity = '0.7'
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)'
                }}
              >
                {currentDownload.secondary.label}
              </a>
            )}
          </div>
          <div className="cta-platforms" style={{ flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
              <span>Or install CLI:</span>
              <code style={{ background: '#1f2937', color: '#10b981', padding: '8px 16px', borderRadius: '6px', fontFamily: 'Monaco, monospace' }}>npm install -g vibecodingmachine-cli</code>
            </div>
            <div style={{ fontSize: '0.9rem', opacity: '0.8', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
              <span>{currentDownload.installer.text}</span>
              <a
                href={currentDownload.installer.url}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: '#5a7c59',
                  color: 'white',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '500',
                  fontSize: '0.95rem',
                  transition: 'background 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#6a8c69'
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#5a7c59'
                }}
              >
                <span style={{ fontSize: '1.2rem' }}>☁️</span>
                {currentDownload.installer.linkText}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default CTA
